import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { RouterProvider } from "react-router";
import { createProjectRouter, createRootRouter } from "./routes";
import { AccessGate } from "./components/access-gate";
import "./index.css";
import { setProjectSlug } from "./lib/api";
import { projectFromPath } from "./lib/project-url";
import { initializeTheme } from "./stores/theme-store";

const stopThemeSync = initializeTheme();
if (import.meta.hot) import.meta.hot.dispose(stopThemeSync);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is considered fresh for 30s, so navigating back to a page you just
      // left doesn't refetch. Tune per-query where a screen needs it fresher.
      staleTime: 30_000,
      // Off because it surprises people: with it on, alt-tabbing back to the
      // browser silently refires every visible query.
      refetchOnWindowFocus: false,
      // Retrying a 404 or a 422 is pointless — the answer will not change. Only
      // retry what might be transient.
      retry: (failureCount, error) => {
        const status = (error as { status?: number }).status;
        if (status !== undefined && status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

// The address picks the router, once: inside `/p/<slug>` the project's pages run
// under that basename and lib/api.ts sends project-scoped calls to
// `/projects/<slug>/...`. Moving to another project is a full page load, so
// neither ever changes while the app is running.
const project = projectFromPath(window.location.pathname);
setProjectSlug(project);
const router = project ? createProjectRouter(project) : createRootRouter();

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      {/* Above both routers: a turned-off account sees one screen wherever it is. */}
      <AccessGate>
        <RouterProvider router={router} />
      </AccessGate>
      {/* Query Devtools: the flower icon in the corner shows every query, its
          cache state and its data. When a screen shows stale or missing data,
          open this before adding a console.log — it usually says exactly which
          query is stale and why. `import.meta.env.DEV` is statically replaced at
          build time, so Rollup drops this and the whole devtools package from
          the production bundle. */}
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  </StrictMode>,
);
